import React, { useState, useEffect } from 'react';
import axios from '../axiosConfig';
import * as XLSX from 'xlsx';
import GroundResponseDetail from './seismic/GroundResponseDetail';
import LPISummaryCards, { lpiLevel } from './seismic/LPISummaryCards';

/**
 * 지반응답해석 결과 시각화
 *
 * FL 결과와 LPI 결과를 테이블로 표시하고 Excel 다운로드 제공
 */
const AnalysisResultView = ({ outputDir, onBack, boreholes = [] }) => {
  const [flResult, setFlResult] = useState([]);
  const [lpiResult, setLpiResult] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary'); // 'summary' | 'lpi' | 'fl'
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  // 목록에서 시추공 한 개를 고르면 상세 해석 화면으로 내려간다.
  const [detailBorehole, setDetailBorehole] = useState(null);

  // 결과 CSV 는 시추공 '이름'만 담고 있으므로, 상세 해석 호출에 필요한 DB id 를 이름으로 찾는다.
  const findBoreholeByName = (name) =>
    boreholes.find((b) => String(b.name) === String(name)) || null;

  const openDetail = (name) => {
    const borehole = findBoreholeByName(name);
    if (!borehole) {
      alert(`시추공 '${name}' 정보를 찾을 수 없어 상세 해석을 열 수 없습니다.`);
      return;
    }
    setDetailBorehole({ id: borehole.id, name: borehole.name });
  };

  useEffect(() => {
    fetchResults();
  }, [outputDir]);

  const fetchResults = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/seismic/results', {
        params: { output_dir: outputDir }
      });

      setFlResult(response.data.data.fl_result);
      setLpiResult(response.data.data.lpi_result);
    } catch (error) {
      console.error('Error fetching results:', error);
      alert('결과를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      // 숫자 비교
      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // 문자열 비교
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const downloadExcel = (data, filename) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Results');
    XLSX.writeFile(wb, filename);
  };

  // 시추공 하나를 고르면 배치 결과 대신 상세 해석 화면을 띄운다.
  if (detailBorehole) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <GroundResponseDetail
            boreholeId={detailBorehole.id}
            boreholeName={detailBorehole.name}
            onBack={() => setDetailBorehole(null)}
          />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">결과를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  const lpiColumns = lpiResult.length > 0 ? Object.keys(lpiResult[0]) : [];
  const flColumns = flResult.length > 0 ? Object.keys(flResult[0]) : [];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">지반응답해석 결과</h1>
            <p className="text-gray-600">액상화 평가 결과를 확인하고 다운로드할 수 있습니다.</p>
          </div>
          <button
            onClick={onBack}
            className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            ← 돌아가기
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto mb-4">
        <div className="flex space-x-2 border-b border-gray-300">
          <button
            onClick={() => setActiveTab('summary')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'summary'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            해석 요약
          </button>
          <button
            onClick={() => setActiveTab('lpi')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'lpi'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            LPI 결과 ({lpiResult.length}개)
          </button>
          <button
            onClick={() => setActiveTab('fl')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'fl'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            FL 결과 ({flResult.length}개)
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-lg">
        {/* Download Button */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">
            {activeTab === 'summary'
              ? '시추공별 액상화위험지수 요약'
              : activeTab === 'lpi'
              ? 'LPI (Liquefaction Potential Index)'
              : 'FL (Factor of Safety)'}
          </h2>
          {activeTab === 'summary' ? (
            <span className="text-sm text-gray-500">
              카드를 선택하면 해당 시추공의 심도별 상세 해석으로 이동합니다
            </span>
          ) : (
            <button
              onClick={() =>
                downloadExcel(
                  activeTab === 'lpi' ? lpiResult : flResult,
                  activeTab === 'lpi' ? 'LPI_result.xlsx' : 'FL_result.xlsx'
                )
              }
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              Excel 다운로드
            </button>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {activeTab === 'summary' ? (
            <div className="p-6 bg-gray-50">
              <LPISummaryCards lpiResult={lpiResult} onSelect={openDetail} />
            </div>
          ) : activeTab === 'lpi' ? (
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  {lpiColumns.map((col) => (
                    <th
                      key={col}
                      onClick={() => handleSort(col)}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                    >
                      <div className="flex items-center space-x-1">
                        <span>{col}</span>
                        {sortConfig.key === col && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {getSortedData(lpiResult).map((row, index) => (
                  <tr
                    key={index}
                    onClick={() => openDetail(row['Borehole_name'])}
                    title="클릭하면 이 시추공의 지반응답해석 상세를 봅니다"
                    className="hover:bg-blue-50 cursor-pointer"
                  >
                    {lpiColumns.map((col) => {
                      // LPI 값 칸은 위험 등급 색으로 강조한다.
                      if (col === 'LPI' || col === 'LPI_acc') {
                        const lv = lpiLevel(row[col]);
                        const tone = {
                          green: 'text-green-600',
                          yellow: 'text-yellow-600',
                          orange: 'text-orange-600',
                          red: 'text-red-600',
                          gray: 'text-gray-400',
                        }[lv.tone];
                        return (
                          <td key={col} className={`px-4 py-3 text-sm font-semibold tabular-nums ${tone}`}>
                            {row[col]}
                            {col === 'LPI' && (
                              <span className="ml-2 text-[11px] font-normal text-gray-400">
                                {lv.label}
                              </span>
                            )}
                          </td>
                        );
                      }
                      return (
                        <td key={col} className="px-4 py-3 text-sm text-gray-900">
                          {row[col]}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  {flColumns.map((col) => (
                    <th
                      key={col}
                      onClick={() => handleSort(col)}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                    >
                      <div className="flex items-center space-x-1">
                        <span>{col}</span>
                        {sortConfig.key === col && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {getSortedData(flResult).map((row, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    {flColumns.map((col) => (
                      <td key={col} className="px-4 py-3 text-sm text-gray-900">
                        {row[col]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Empty State */}
        {((activeTab === 'lpi' && lpiResult.length === 0) ||
          (activeTab === 'fl' && flResult.length === 0)) && (
          <div className="p-12 text-center">
            <p className="text-gray-500">결과 데이터가 없습니다.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisResultView;
